import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  AlertTriangle, User, MapPin, Globe, Flag, Check, X, Search, 
  ArrowLeft, RefreshCw, ChevronDown, ChevronUp, Shield, UserX, CheckCircle2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Country code to full name mapping
const COUNTRY_NAMES = {
  'US': 'United States',
  'KR': 'South Korea',
  'JP': 'Japan',
  'CN': 'China',
  'TW': 'Taiwan',
  'HK': 'Hong Kong',
  'SG': 'Singapore',
  'PH': 'Philippines',
  'TH': 'Thailand',
  'MY': 'Malaysia',
  'ID': 'Indonesia',
  'VN': 'Vietnam',
  'IN': 'India',
  'LK': 'Sri Lanka',
  'NP': 'Nepal',
  'KH': 'Cambodia',
  'MN': 'Mongolia',
  'BN': 'Brunei',
  'MV': 'Maldives',
  'AU': 'Australia',
  'NZ': 'New Zealand',
  'FJ': 'Fiji',
  'PG': 'Papua New Guinea',
  'GU': 'Guam',
  'GB': 'United Kingdom',
  'DE': 'Germany',
  'FR': 'France',
  'IT': 'Italy',
  'ES': 'Spain',
  'NL': 'Netherlands',
  'BE': 'Belgium',
  'AT': 'Austria',
  'CH': 'Switzerland',
  'PT': 'Portugal',
  'IE': 'Ireland',
  'LU': 'Luxembourg',
  'MC': 'Monaco',
  'SE': 'Sweden',
  'NO': 'Norway',
  'DK': 'Denmark',
  'FI': 'Finland',
  'IS': 'Iceland',
  'PL': 'Poland',
  'CZ': 'Czech Republic',
  'SK': 'Slovakia',
  'HU': 'Hungary',
  'RO': 'Romania',
  'BG': 'Bulgaria',
  'HR': 'Croatia',
  'SI': 'Slovenia',
  'RS': 'Serbia',
  'BA': 'Bosnia and Herzegovina',
  'MK': 'North Macedonia',
  'AL': 'Albania',
  'LT': 'Lithuania',
  'LV': 'Latvia',
  'EE': 'Estonia',
  'GR': 'Greece',
  'CY': 'Cyprus',
  'MT': 'Malta',
  'UA': 'Ukraine',
  'GE': 'Georgia',
  'AM': 'Armenia',
  'AZ': 'Azerbaijan',
  'TR': 'Turkey',
  'AE': 'United Arab Emirates',
  'SA': 'Saudi Arabia',
  'IL': 'Israel',
  'QA': 'Qatar',
  'KW': 'Kuwait',
  'BH': 'Bahrain',
  'OM': 'Oman',
  'JO': 'Jordan',
  'EG': 'Egypt',
  'CA': 'Canada',
  'MX': 'Mexico',
  'BR': 'Brazil',
  'AR': 'Argentina',
  'CL': 'Chile',
  'CO': 'Colombia',
  'PE': 'Peru',
  'EC': 'Ecuador',
  'UY': 'Uruguay',
  'CR': 'Costa Rica',
  'PA': 'Panama',
  'GT': 'Guatemala',
  'DO': 'Dominican Republic',
  'PR': 'Puerto Rico',
  'JM': 'Jamaica',
  'TT': 'Trinidad and Tobago',
  'BS': 'Bahamas',
  'ZA': 'South Africa',
  'NG': 'Nigeria',
  'MA': 'Morocco',
  'KE': 'Kenya',
  'GH': 'Ghana',
  'TN': 'Tunisia',
  'DZ': 'Algeria',
  'TZ': 'Tanzania',
  'UG': 'Uganda',
  'SN': 'Senegal',
  'MU': 'Mauritius',
  'USA': 'United States', // Legacy support
  'OTHER': 'OTHER'
};

// Helper to get full country name
const getCountryName = (code) => {
  if (!code) return null;
  return COUNTRY_NAMES[code] || code;
};

export default function AdminProfileReview() {
  const { getAuthHeader } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [expandedUser, setExpandedUser] = useState(null);
  const [flagging, setFlagging] = useState(null);
  const [customReason, setCustomReason] = useState('');
  const [requireAdminApproval, setRequireAdminApproval] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users-needing-review`, {
        headers: getAuthHeader()
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleFlag = async (userId, reason, adminApprovalRequired = false) => {
    setFlagging(userId);
    try {
      const params = new URLSearchParams();
      params.append('reason', reason || 'Profile requires verification');
      if (adminApprovalRequired) {
        params.append('require_admin_approval', 'true');
      }
      
      await axios.post(`${API}/admin/users/${userId}/flag-profile?${params.toString()}`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`User flagged${adminApprovalRequired ? ' (admin approval required)' : ''}`);
      fetchUsers();
      setCustomReason('');
      setRequireAdminApproval(false);
    } catch (error) {
      toast.error('Failed to flag user');
    } finally {
      setFlagging(null);
    }
  };

  const handleUnflag = async (userId) => {
    setFlagging(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/unflag-profile`, {}, {
        headers: getAuthHeader()
      });
      toast.success('User unflagged successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to unflag user');
    } finally {
      setFlagging(null);
    }
  };

  const handleApproveField = async (userId, field) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/approve-field?field=${field}`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`Field approved`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to approve field');
    }
  };

  const handleUnapproveField = async (userId, field) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/unapprove-field?field=${field}`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`Approval removed`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to remove approval');
    }
  };

  const isFieldApproved = (user, field) => user.approved_fields?.includes(field);

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || 
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const hasCountryIssue = !user.country || user.country === 'OTHER' || user.country.trim() === '';
    const needsReview = user.profile_status === 'corrected_needs_review';
    
    const matchesFilter = 
      filter === 'all' ||
      (filter === 'flagged' && user.profile_flagged) ||
      (filter === 'corrected' && needsReview) ||
      (filter === 'unflagged' && !user.profile_flagged && !needsReview) ||
      (filter === 'country-issue' && hasCountryIssue);
    
    return matchesSearch && matchesFilter;
  });

  const flaggedCount = users.filter(u => u.profile_flagged).length;
  const correctedCount = users.filter(u => u.profile_status === 'corrected_needs_review').length;
  const unflaggedCount = users.filter(u => !u.profile_flagged && u.profile_status !== 'corrected_needs_review').length;
  const countryIssueCount = users.filter(u => !u.country || u.country === 'OTHER' || u.country.trim() === '').length;

  const StatCard = ({ label, value, color, active, onClick }) => (
    <button
      onClick={onClick}
      className={`flex-1 p-4 border transition-all ${
        active 
          ? `border-${color}-500 bg-${color}-500/10` 
          : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'
      }`}
    >
      <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">{label}</p>
      <p className={`font-mono text-2xl font-bold mt-1 ${active ? `text-${color}-500` : 'text-white'}`}>
        {value}
      </p>
    </button>
  );

  const FieldRow = ({ label, value, fieldName, user, isRequired, showApprove = true }) => {
    const approved = isFieldApproved(user, fieldName);
    const isEmpty = !value || value.trim() === '';
    // For country field, check raw user.country for OTHER since value is now the full name
    const isInvalid = fieldName === 'country' && (user.country === 'OTHER' || isEmpty);
    
    return (
      <div className={`flex items-center justify-between py-3 px-4 border-b border-zinc-800/50 last:border-0 ${
        isInvalid ? 'bg-red-500/5' : ''
      }`}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">{label}</span>
            {approved && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 bg-green-500/20 text-green-400 font-mono text-[9px]">
                <CheckCircle2 className="w-2.5 h-2.5" />
                APPROVED
              </span>
            )}
            {isInvalid && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 bg-red-500/20 text-red-400 font-mono text-[9px]">
                <AlertTriangle className="w-2.5 h-2.5" />
                REQUIRED
              </span>
            )}
          </div>
          <p className={`font-mono text-sm truncate ${
            isEmpty ? 'text-zinc-600 italic' : isInvalid ? 'text-red-400' : 'text-white'
          }`}>
            {isEmpty ? 'Not set' : (fieldName === 'country' && user.country === 'OTHER') ? 'OTHER (needs real country)' : value}
          </p>
        </div>
        {showApprove && !isInvalid && (
          <div className="ml-4 flex-shrink-0">
            {approved ? (
              <button
                onClick={(e) => { e.stopPropagation(); handleUnapproveField(user.id, fieldName); }}
                className="font-mono text-[10px] px-3 py-1.5 border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors"
              >
                REVOKE
              </button>
            ) : (
              <button
                onClick={(e) => { e.stopPropagation(); handleApproveField(user.id, fieldName); }}
                className="font-mono text-[10px] px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white transition-colors"
              >
                APPROVE
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <div className="border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-5">
              <Link 
                to="/admin" 
                className="w-10 h-10 flex items-center justify-center border border-zinc-800 hover:border-zinc-600 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 text-zinc-400" />
              </Link>
              <div>
                <h1 className="font-mono text-lg font-bold tracking-tight flex items-center gap-3">
                  <Shield className="w-5 h-5 text-tiffany-500" />
                  PROFILE REVIEW
                </h1>
                <p className="font-mono text-xs text-zinc-500 mt-0.5">
                  Review suspicious profiles and flag users for correction
                </p>
              </div>
            </div>
            <button
              onClick={fetchUsers}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-zinc-800 hover:border-zinc-600 font-mono text-xs text-zinc-400 hover:text-white transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              REFRESH
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="grid grid-cols-5 gap-3">
          <button
            onClick={() => setFilter('all')}
            className={`p-4 border transition-all text-left ${
              filter === 'all' 
                ? 'border-tiffany-500 bg-tiffany-500/5' 
                : 'border-zinc-800 bg-zinc-900/30 hover:border-zinc-700'
            }`}
          >
            <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">Total</p>
            <p className={`font-mono text-2xl font-bold mt-1 ${filter === 'all' ? 'text-tiffany-500' : 'text-white'}`}>
              {users.length}
            </p>
          </button>
          
          <button
            onClick={() => setFilter('flagged')}
            className={`p-4 border transition-all text-left ${
              filter === 'flagged' 
                ? 'border-red-500 bg-red-500/5' 
                : 'border-zinc-800 bg-zinc-900/30 hover:border-zinc-700'
            }`}
          >
            <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">Flagged</p>
            <p className={`font-mono text-2xl font-bold mt-1 ${filter === 'flagged' ? 'text-red-500' : 'text-white'}`}>
              {flaggedCount}
            </p>
          </button>
          
          <button
            onClick={() => setFilter('corrected')}
            className={`p-4 border transition-all text-left ${
              filter === 'corrected' 
                ? 'border-blue-500 bg-blue-500/5' 
                : 'border-zinc-800 bg-zinc-900/30 hover:border-zinc-700'
            }`}
          >
            <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">Corrected</p>
            <p className={`font-mono text-2xl font-bold mt-1 ${filter === 'corrected' ? 'text-blue-500' : 'text-white'}`}>
              {correctedCount}
            </p>
          </button>
          
          <button
            onClick={() => setFilter('unflagged')}
            className={`p-4 border transition-all text-left ${
              filter === 'unflagged' 
                ? 'border-yellow-500 bg-yellow-500/5' 
                : 'border-zinc-800 bg-zinc-900/30 hover:border-zinc-700'
            }`}
          >
            <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">Issues</p>
            <p className={`font-mono text-2xl font-bold mt-1 ${filter === 'unflagged' ? 'text-yellow-500' : 'text-white'}`}>
              {unflaggedCount}
            </p>
          </button>
          
          <button
            onClick={() => setFilter('country-issue')}
            className={`p-4 border transition-all text-left ${
              filter === 'country-issue' 
                ? 'border-orange-500 bg-orange-500/5' 
                : 'border-zinc-800 bg-zinc-900/30 hover:border-zinc-700'
            }`}
          >
            <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">No Country</p>
            <p className={`font-mono text-2xl font-bold mt-1 ${filter === 'country-issue' ? 'text-orange-500' : 'text-white'}`}>
              {countryIssueCount}
            </p>
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-6xl mx-auto px-6 pb-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
          <input
            type="text"
            placeholder="Search by email or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full h-11 pl-11 pr-4 bg-zinc-900/50 border border-zinc-800 text-white font-mono text-sm placeholder:text-zinc-600 focus:outline-none focus:border-zinc-600 transition-colors"
          />
        </div>
      </div>

      {/* User List */}
      <div className="max-w-6xl mx-auto px-6 pb-12">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 border border-zinc-800 bg-zinc-900/20">
            <div className="w-8 h-8 border-2 border-tiffany-500 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="font-mono text-sm text-zinc-500">Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 border border-zinc-800 bg-zinc-900/20">
            <CheckCircle2 className="w-12 h-12 text-zinc-700 mb-4" />
            <p className="font-mono text-sm text-zinc-500">No users match your criteria</p>
          </div>
        ) : (
          <div className="border border-zinc-800 divide-y divide-zinc-800">
            {filteredUsers.map(user => (
              <div key={user.id} className={
                user.profile_flagged ? 'bg-red-500/5' : 
                user.profile_status === 'corrected_needs_review' ? 'bg-blue-500/5' : ''
              }>
                {/* User Row */}
                <button
                  onClick={() => setExpandedUser(expandedUser === user.id ? null : user.id)}
                  className="w-full p-4 flex items-center justify-between hover:bg-zinc-900/50 transition-colors text-left"
                >
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 ${
                      user.profile_flagged ? 'bg-red-500/20' : 
                      user.profile_status === 'corrected_needs_review' ? 'bg-blue-500/20' : 'bg-zinc-800'
                    }`}>
                      {user.profile_flagged ? (
                        <UserX className="w-5 h-5 text-red-500" />
                      ) : user.profile_status === 'corrected_needs_review' ? (
                        <User className="w-5 h-5 text-blue-500" />
                      ) : (
                        <User className="w-5 h-5 text-zinc-500" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm text-white truncate">{user.email}</span>
                        {user.profile_flagged && (
                          <span className={`px-2 py-0.5 ${user.require_admin_approval ? 'bg-orange-500/20 text-orange-400' : 'bg-red-500/20 text-red-400'} font-mono text-[9px] flex-shrink-0`}>
                            {user.require_admin_approval ? 'LOCKED' : 'FLAGGED'}
                          </span>
                        )}
                        {user.profile_status === 'corrected_needs_review' && (
                          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 font-mono text-[9px] flex-shrink-0">
                            CORRECTED
                          </span>
                        )}
                        {user.is_international && (
                          <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 font-mono text-[9px] flex-shrink-0">
                            INTL
                          </span>
                        )}
                      </div>
                      <p className="font-mono text-xs text-zinc-500 truncate mt-0.5">
                        {user.full_name || 'No name'} {user.country ? `- ${getCountryName(user.country)}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                    {user.detected_issues?.length > 0 && (
                      <span className="px-2 py-1 bg-yellow-500/10 border border-yellow-500/30 text-yellow-500 font-mono text-[10px]">
                        {user.detected_issues.length} {user.detected_issues.length === 1 ? 'ISSUE' : 'ISSUES'}
                      </span>
                    )}
                    {expandedUser === user.id ? (
                      <ChevronUp className="w-4 h-4 text-zinc-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-zinc-500" />
                    )}
                  </div>
                </button>

                {/* Expanded Panel */}
                {expandedUser === user.id && (
                  <div className="border-t border-zinc-800 bg-zinc-900/30">
                    {/* Issues Alert */}
                    {user.detected_issues?.length > 0 && (
                      <div className="m-4 p-3 bg-yellow-500/10 border border-yellow-500/30">
                        <p className="font-mono text-[10px] text-yellow-500 uppercase tracking-wider mb-2">
                          Detected Issues
                        </p>
                        <ul className="space-y-1">
                          {user.detected_issues.map((issue, idx) => (
                            <li key={idx} className="font-mono text-xs text-yellow-400/80 flex items-start gap-2">
                              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              <span>{issue}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Profile Fields */}
                    <div className="mx-4 mb-4 border border-zinc-800 bg-zinc-900/50">
                      <FieldRow 
                        label="Full Name" 
                        value={user.full_name} 
                        fieldName="full_name" 
                        user={user}
                      />
                      <FieldRow 
                        label="Country" 
                        value={getCountryName(user.country)} 
                        fieldName="country" 
                        user={user}
                        showApprove={false}
                      />
                      {/* Home Country - for international customers using US forwarding */}
                      {user.is_international && user.country === 'US' && (
                        <div className="py-3 px-4 border-b border-zinc-800/50 last:border-0">
                          <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">Home Country</span>
                          <p className={`font-mono text-sm mt-1 ${user.home_country ? 'text-white' : 'text-red-400'}`}>
                            {user.home_country ? getCountryName(user.home_country) : 'NOT SET - Required for international customers'}
                          </p>
                        </div>
                      )}
                      <FieldRow 
                        label="Street Address" 
                        value={user.address} 
                        fieldName="address" 
                        user={user}
                      />
                      {user.is_international && (
                        <FieldRow 
                          label="Apt/Unit (Forwarding ID)" 
                          value={user.apt_unit} 
                          fieldName="apt_unit" 
                          user={user}
                          isRequired={true}
                        />
                      )}
                      <div className="py-3 px-4 border-b border-zinc-800/50 last:border-0">
                        <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">Location</span>
                        <p className="font-mono text-sm text-white mt-1">
                          {[user.city, user.state, user.zip_code].filter(Boolean).join(', ') || 'Not set'}
                        </p>
                      </div>
                    </div>

                    {/* Flag Reason */}
                    {user.profile_flagged && user.flag_reason && (
                      <div className={`mx-4 mb-4 p-3 ${user.require_admin_approval ? 'bg-orange-500/10 border-orange-500/30' : 'bg-red-500/10 border-red-500/30'} border`}>
                        <p className={`font-mono text-[10px] ${user.require_admin_approval ? 'text-orange-400' : 'text-red-400'} uppercase tracking-wider`}>
                          Flag Reason {user.require_admin_approval && '(LOCKED - ADMIN APPROVAL REQUIRED)'}
                        </p>
                        <p className={`font-mono text-sm ${user.require_admin_approval ? 'text-orange-300' : 'text-red-300'} mt-1`}>{user.flag_reason}</p>
                        {user.flagged_at && (
                          <p className="font-mono text-[10px] text-zinc-500 mt-2">
                            Flagged: {new Date(user.flagged_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Corrected Status */}
                    {user.profile_status === 'corrected_needs_review' && (
                      <div className="mx-4 mb-4 p-3 bg-blue-500/10 border border-blue-500/30">
                        <p className="font-mono text-[10px] text-blue-400 uppercase tracking-wider">Corrected - Needs Review</p>
                        <p className="font-mono text-sm text-blue-300 mt-1">
                          User submitted corrected info. Please verify it's not random text.
                        </p>
                        {user.corrected_at && (
                          <p className="font-mono text-[10px] text-zinc-500 mt-2">
                            Corrected: {new Date(user.corrected_at).toLocaleString()}
                          </p>
                        )}
                        {user.flag_reason && (
                          <p className="font-mono text-[10px] text-zinc-500 mt-1">
                            Original flag: {user.flag_reason}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="p-4 border-t border-zinc-800 flex items-center gap-3">
                      {user.profile_flagged ? (
                        <button
                          onClick={() => handleUnflag(user.id)}
                          disabled={flagging === user.id}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white font-mono text-xs transition-colors disabled:opacity-50"
                        >
                          {flagging === user.id ? (
                            <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Check className="w-3.5 h-3.5" />
                          )}
                          REMOVE FLAG
                        </button>
                      ) : user.profile_status === 'corrected_needs_review' ? (
                        <>
                          <button
                            onClick={() => handleUnflag(user.id)}
                            disabled={flagging === user.id}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white font-mono text-xs transition-colors disabled:opacity-50"
                          >
                            {flagging === user.id ? (
                              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            )}
                            APPROVE PROFILE
                          </button>
                          <button
                            onClick={() => handleFlag(user.id, 'Profile corrections insufficient')}
                            disabled={flagging === user.id}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white font-mono text-xs transition-colors disabled:opacity-50"
                          >
                            {flagging === user.id ? (
                              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Flag className="w-3.5 h-3.5" />
                            )}
                            RE-FLAG
                          </button>
                        </>
                      ) : (
                        <div className="flex flex-col gap-3 w-full">
                          <input
                            type="text"
                            placeholder="Flag reason (optional)"
                            value={customReason}
                            onChange={(e) => setCustomReason(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="flex-1 h-9 px-3 bg-zinc-800 border border-zinc-700 text-white font-mono text-xs placeholder:text-zinc-600 focus:outline-none focus:border-zinc-500"
                          />
                          
                          {/* Require Admin Approval Checkbox */}
                          <div className="flex items-start gap-3 p-3 bg-orange-500/10 border border-orange-500/30">
                            <input
                              type="checkbox"
                              id={`require-admin-approval-${user.id}`}
                              checked={requireAdminApproval}
                              onChange={(e) => setRequireAdminApproval(e.target.checked)}
                              onClick={(e) => e.stopPropagation()}
                              className="mt-0.5 w-4 h-4 accent-orange-500"
                              data-testid={`require-admin-approval-${user.id}`}
                            />
                            <div>
                              <label 
                                htmlFor={`require-admin-approval-${user.id}`} 
                                className="font-mono text-xs text-orange-400 cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                REQUIRE ADMIN APPROVAL
                              </label>
                              <p className="font-mono text-[10px] text-zinc-500 mt-0.5">
                                User cannot self-unflag. Only admin can remove.
                              </p>
                            </div>
                          </div>
                          
                          <button
                            onClick={() => handleFlag(user.id, customReason, requireAdminApproval)}
                            disabled={flagging === user.id}
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white font-mono text-xs transition-colors disabled:opacity-50"
                          >
                            {flagging === user.id ? (
                              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Flag className="w-3.5 h-3.5" />
                            )}
                            FLAG USER
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
